import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, FileClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface AuditRow {
  id: string;
  action: string;
  actor_type: string;
  actor_id: string | null;
  actor_email: string | null;
  subject_user_id: string | null;
  subject_email: string | null;
  source: string | null;
  details: Record<string, any>;
  created_at: string;
}

const ACTIONS = ["all", "warn", "final_notice", "anonymize", "purge", "dismiss", "stamp_reset", "review_queued"];

const actionStyle: Record<string, string> = {
  warn: "bg-amber-500/15 text-amber-300",
  final_notice: "bg-orange-500/15 text-orange-300",
  anonymize: "bg-sky-500/15 text-sky-300",
  purge: "bg-destructive/15 text-destructive",
  dismiss: "bg-muted text-muted-foreground",
  stamp_reset: "bg-primary/10 text-primary",
  review_queued: "bg-secondary text-secondary-foreground",
};

const RetentionAuditLog = () => {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("retention_audit_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (filter !== "all") q = q.eq("action", filter);
    const { data, error } = await q;
    setLoading(false);
    if (error) toast.error(error.message);
    else setRows((data as AuditRow[]) ?? []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-xl font-semibold">Retention audit log</h2>
          <p className="text-sm text-muted-foreground">Every warn, notice, anonymize, purge, and dismissal — last 200 events.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-xl">
          <FileClock className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No audit events yet.</p>
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2">Actor</th>
                <th className="text-left px-4 py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionStyle[r.action] ?? "bg-muted text-muted-foreground"}`}>
                      {r.action}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-foreground">{r.subject_email ?? "—"}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[180px]">{r.subject_user_id ?? ""}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-foreground">{r.actor_email ?? r.actor_type}</div>
                    <div className="text-xs text-muted-foreground">{r.actor_type}{r.source ? ` · ${r.source}` : ""}</div>
                  </td>
                  <td className="px-4 py-2">
                    {r.details && Object.keys(r.details).length > 0 ? (
                      <details>
                        <summary className="text-xs text-muted-foreground cursor-pointer">view</summary>
                        <pre className="mt-2 text-[11px] bg-muted/30 p-2 rounded overflow-x-auto max-w-md">
                          {JSON.stringify(r.details, null, 2)}
                        </pre>
                      </details>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RetentionAuditLog;